import React from "react";
import { Sun, Waves, Wrench, Coffee } from "lucide-react";
import usePageMeta from "@/lib/seo";

const pillars = [
  { icon: Waves, title: "Coastal, not kitsch", body: "We skip the seashell-painted-sign aesthetic. Linen, weathered teak, brass, charcoal." },
  { icon: Sun, title: "Built for everyday", body: "Gear we'd actually carry — not safe-queens, not influencer props." },
  { icon: Wrench, title: "Lived-in, not new-looking", body: "We love products that patina. Tools that mark up. Stuff that earns scars." },
  { icon: Coffee, title: "Slow weekends", body: "The whole brand is built around how a Saturday morning should feel on the Gulf." },
];

export default function AboutPage() {
  usePageMeta({ title: "About", description: "Built to feel lived in. Rugged Gulf Coast finds for backwater living, dock life, outdoor gear, and everyday carry." });
  return (
    <div data-testid="about-page">
      <section className="relative h-[55vh] min-h-[400px] overflow-hidden">
        <img src="https://customer-assets.emergentagent.com/job_gulf-coast-home/artifacts/kzprqj2r_IMG_8600.png" alt="Marsh dock at sunrise — Gulf Coast Home Co." className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 flex items-end px-6 md:px-12 pb-12 max-w-7xl mx-auto left-0 right-0">
          <div>
            <p className="text-[#EBAA86] text-xs uppercase tracking-[0.3em] mb-3">About</p>
            <h1 className="font-heading text-5xl md:text-6xl text-white font-light tracking-tight max-w-2xl">
              Built to feel lived in.
            </h1>
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 md:px-12 py-20">
        <p className="text-lg md:text-xl text-[#2D3032] leading-relaxed mb-6">
          Gulf Coast Home Co. is rugged Gulf Coast finds for backwater living, dock life, outdoor gear, and everyday carry. No polished showroom energy — just the stuff that earns its place on the boat, on the porch, and in the truck.
        </p>
        <p className="text-[#636769] leading-relaxed mb-6">
          We started this because nothing online matched how we actually live down here. Marina mornings, oyster-shell driveways, rope-and-lantern dockside evenings, cast iron over an open fire. Most "lifestyle" brands either gloss it up or LARP it. We just wanted a real one.
        </p>
        <p className="text-[#636769] leading-relaxed">
          So this is the home for the gear we use, the boards we pin, and the slow weekends we plan around. Built to feel lived in — because it is.
        </p>
      </section>

      <section className="bg-white border-y border-[#E6E1D6]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
          <p className="text-xs uppercase tracking-[0.24em] text-[#8EAD9E] mb-3">What we stand for</p>
          <h2 className="font-heading text-3xl md:text-4xl text-[#2D3032] font-normal mb-12 max-w-2xl">Four ideas we keep coming back to.</h2>
          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            {pillars.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.title} className="flex gap-5">
                  <span className="w-12 h-12 bg-[#F4F1EA] border border-[#E6E1D6] flex items-center justify-center text-[#8EAD9E] flex-shrink-0">
                    <Icon size={20} strokeWidth={1.5} />
                  </span>
                  <div>
                    <h3 className="font-heading text-lg font-medium text-[#2D3032] mb-2">{p.title}</h3>
                    <p className="text-sm text-[#636769] leading-relaxed">{p.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
